import { AppConfig } from "./AppConfig.js";
import { ApiCache } from "./ApiCache.js";
import { MockService } from "../services/MockService.js";
import { routeKbEndpoint } from "../services/KbApiService.js";

export class ApiService {
  static baseUrl = AppConfig.api.baseUrl;
  static timeout = AppConfig.api.timeout;
  static retryAttempts = AppConfig.api.retryAttempts;

  static async post(endpoint, data, token = null, options = {}) {
    if (AppConfig.api.useMock) return MockService.post(endpoint, data, token, options);
    const kb = routeKbEndpoint(endpoint, data);
    if (kb !== undefined) return kb;
    return this.makeRequest("POST", `${this.baseUrl}/${endpoint}`, data, token, options);
  }

  static async get(endpoint, token = null, options = {}) {
    if (AppConfig.api.useMock) return MockService.get(endpoint, token, options);
    const kb = routeKbEndpoint(endpoint, null);
    if (kb !== undefined) return kb;
    return this.makeRequest("GET", `${this.baseUrl}/${endpoint}`, null, token, options);
  }

  static async postFormData(endpoint, formData, token = null) {
    if (AppConfig.api.useMock) return MockService.postFormData(endpoint, formData, token);
    return this.makeRequest("POST", `${this.baseUrl}/${endpoint}`, formData, token);
  }

  static async postBlob(endpoint, data, token = null) {
    if (AppConfig.api.useMock) return MockService.postBlob(endpoint, data, token);
    const url = `${this.baseUrl}/${endpoint}`;
    const headers = {};
    if (token) headers["x-token"] = token;
    if (data) headers["Content-Type"] = "application/json";

    const response = await fetch(url, {
      method: "POST",
      headers,
      body: data ? JSON.stringify(data) : null,
    });

    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return response.blob();
  }

  static invalidate(endpointContains = "", token = null) {
    ApiCache.invalidate({ urlContains: `${this.baseUrl}/${endpointContains}`, token });
  }

  static async makeRequest(method, url, data = null, token = null, options = {}) {
    const {
      cacheTiempo = "false",
      cachePersistent = true,
      forceRefresh = false,
      dedupe = true,
    } = options;

    const cache = (AppConfig.api.cacheEnabled) ? cacheTiempo : false;
    const ttlMs = ApiCache.parseTTL(cache);
    const cacheKey = ApiCache.buildKey({ method, url, data, token });
    const canCache = ApiCache.canCache({ ttlMs, data });

    if (canCache && !forceRefresh) {
      const cached = ApiCache.read(cacheKey);
      if (cached !== null) return cached;
    }

    if (dedupe) {
      const inflight = ApiCache.getInFlight(cacheKey);
      if (inflight) return inflight;
    }

    const requestPromise = (async() => {
      let lastError;

      for (let attempt = 1; attempt <= this.retryAttempts; attempt++) {
        try {
          const headers = {};

          if (token) headers["x-token"] = token;

          let bodyToSend = data;

          if (data && !(data instanceof FormData)) {
            headers["Content-Type"] = "application/json";
            bodyToSend = JSON.stringify(data);
          }

          const response = await fetch(url, {
            method,
            headers,
            body: bodyToSend || null,
          });

          if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

          const json = await response.json();

          if (canCache) {
            ApiCache.write(cacheKey, json, ttlMs, cachePersistent);
          }

          return json;
        } catch (error) {
          lastError = error;

          if (AppConfig.features?.debug) {
            console.warn(`API request attempt ${attempt} failed:`, error);
          }

          if (attempt === this.retryAttempts) break;

          await new Promise((resolve) => setTimeout(resolve, 1000 * attempt));
        }
      }

      console.error("API Error after all retries:", lastError);
      throw lastError;
    })();

    if (dedupe) ApiCache.setInFlight(cacheKey, requestPromise);

    try {
      return await requestPromise;
    } finally {
      if (dedupe) ApiCache.clearInFlight(cacheKey);
    }
  }
}
